import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SmartFormComponent } from 'ngx-smart-form';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  of,
  skipWhile,
  startWith,
  Subject,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CwbiStatus,
  IPratica,
  Pratica,
  PraticaProgress,
  StatoPratica,
  Target,
} from '../../../interfaces/pratica';
import { BeLiberoProfessionista } from '../../applicant/models/be-professionista';
import { IFactory } from '../../applicant/models/i-factory';
import { BEImpresa, Impresa } from '../../applicant/models/impresa';
import { LiberoProfessionista } from '../../applicant/models/libero-professionista';
import { PersonaGiuridica } from '../../applicant/models/persona-giuridica';
import { ProfessionistaFactory } from '../../applicant/models/professionista-factory';
import { PraticaSection } from '../../pratica/models/pratica-section';
import { StoreData } from '../models/store-data';
import { AuthService } from './auth.service';
import { PersonaGiuridicaService } from './persona-giuridica.service';
import { PraticaService } from './pratica.service';
import { TaskService } from './task.service';
import { ImpresaFactory } from '../../applicant/models/impresa-factory';
import { Product } from '../../requests/models/product';
import { ProductService } from './product.service';
import { trackFormChanges } from '../../_utils/utils';

@Injectable({
  providedIn: 'root',
})
export class PraticaProgressService extends PraticaService {
  private readonly endpoint = environment.api;

  private _selectedPratica = new BehaviorSubject<StoreData<Pratica>>(
    new StoreData()
  );
  selectedPratica$ = this._selectedPratica.asObservable();
  validPratica$ = this._selectedPratica
    .asObservable()
    .pipe(filter((storedData) => storedData.state === 'completed'));

  progress$ = new BehaviorSubject<Record<PraticaSection, number>>({
    applicant: 0,
    requests: 0,
    anagraphics: 0,
    score: 0,
    'presentation-sheet': 0,
    documents: 0,
  });

  total = new BehaviorSubject<number>(0);

  triggerSave$ = new Subject<void>();
  private _sectionHasUpdated$ = new BehaviorSubject<number>(0);

  sectionHasUpdated$ = this._sectionHasUpdated$.asObservable();

  isReadyForSignature$: Observable<boolean>;

  isFrozen = false;

  isOutcome = false;

  hasJustBeenSigned = new Subject<boolean>();

  private _praticaProductsData = new BehaviorSubject(
    new StoreData<Product[]>()
  );

  get praticaProducts$() {
    if (
      this._praticaProductsData.value.state !== 'completed' &&
      this.selectedPratica?.id
    ) {
      return this.getPraticaProducts(this.selectedPratica.id);
    } else {
      return this._praticaProductsData.asObservable();
    }
  }

  get praticaProduct$() {
    return this.praticaProducts$.pipe(
      map((productsData) => productsData.data?.[0])
    );
  }

  constructor(
    http: HttpClient,
    private router: Router,
    protected override auth: AuthService,
    private pgService: PersonaGiuridicaService,
    private productService: ProductService
  ) {
    super(http, auth);
    this.isReadyForSignature$ = combineLatest([
      this.total.pipe(
        map((total) => total === 100),
        distinctUntilChanged()
      ),
      this.validPratica$,
    ]).pipe(
      map(([isCompleted, praticaData]) => {
        if (
          isCompleted &&
          (praticaData.data?.state === StatoPratica.CONFERMATA ||
            (praticaData.data?.type === 'AML' && StatoPratica.APERTA) ||
            praticaData.data?.state === StatoPratica.AGGIUNTA_GARANTE) &&
          this.auth.me?.id === this.selectedPratica?.user
        ) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  private getPraticaProducts(
    praticaId: string
  ): Observable<StoreData<Product[]>> {
    this._praticaProductsData.next(
      this._praticaProductsData.value.update('waiting')
    );
    let request$ = this.productService.list(praticaId);
    return request$.pipe(
      tap((loanProducts) =>
        this._praticaProductsData.next(
          this._praticaProductsData.value.update('completed', loanProducts)
        )
      ),
      catchError((err) => {
        this._praticaProductsData.next(
          this._praticaProductsData.value.update('error', [])
        );
        return of([]);
      }),
      switchMap(() => this._praticaProductsData.asObservable())
    );
  }

  getPratica(id: string | undefined = this.selectedPratica!.id) {
    return this.getById(id).pipe(
      switchMap((pratica) => {
        return this.productService
          .list(pratica.id)
          .pipe(map((products) => new Pratica(pratica, products)));
      }),
      tap((pratica) => {
        if (pratica) {
          this.performPraticaStateChecks(pratica);
          this.init(pratica.progress);
        } else {
          throw 'Pratica non trovata';
        }
        this._selectedPratica.next(
          this._selectedPratica.value.update('completed', pratica)
        );
      }),
      catchError((err) => {
        this.router.navigate(['home', 'requests']);
        console.error(err);
        return throwError(() => 'Pratica non trovata');
      })
    );
  }

  private init(progress: Partial<PraticaProgress> | undefined) {
    let p: Record<PraticaSection, number> = {
      applicant: progress?.applicant || 0,
      requests: progress?.requests || 0,
      anagraphics: progress?.anagraphics || 0,
      score: progress?.score || 0,
      'presentation-sheet': progress?.presentationSheet || 0,
      documents: progress?.documents || 0,
    };

    this.progress$.next(p);

    //this.total.next(progress?.total || 0);
  }

  aggregateProgress(
    componentList: SmartFormComponent[],
    section: PraticaSection
  ) {
    return trackFormChanges(componentList).pipe(
      tap((progress) => {
        this.updateProgress(section, progress);
      })
    );
  }

  updateProgress(section: PraticaSection, progress: number) {
    let p: Record<PraticaSection, number> = {
      ...this.progress,
      [section]: progress,
    };
    this.progress$.next(p);
  }

  saveProgress() {
    this.triggerSave$.next();
  }

  notifySectionUpdate() {
    this._sectionHasUpdated$.next(this._sectionHasUpdated$.value + 1);
  }

  calcTotal() {
    let progressList = Object.values(this.progress);
    let sum = progressList.reduce((p, c) => p + c);
    let media = sum / progressList.length;
    let rounded = Math.floor(media);
    this.total.next(rounded);
  }

  updatePraticaProgress() {
    let body: Partial<IPratica> = {
      progress: {
        ...this.progress,
        presentationSheet: this.progress['presentation-sheet'],
        total: this.total.value,
      },
    };
    if (this.pgService.cachedPersonaGiuridica) {
      body.denominazione = this.pgService.cachedPersonaGiuridica.denominazione;
    }
    return this.edit(this.selectedPratica!.id, body);
  }

  updatePraticaLinkCassettoFiscale(link: string) {
    if (this._selectedPratica.value.data) {
      this._selectedPratica.next(
        this._selectedPratica.value.update('completed', {
          ...this._selectedPratica.value.data,
          linkCassettoFiscale: link,
        })
      );
    }
  }

  updatePraticaCwbi(link: string | null, email: string, status: CwbiStatus) {
    if (this._selectedPratica.value.data) {
      this._selectedPratica.next(
        this._selectedPratica.value.update('completed', {
          ...this._selectedPratica.value.data,
          linkCassettoFiscale: link,
          emailCwbi: email,
          statusCwbi: status,
        })
      );
    }
  }

  get scoreDisabled() {
    return (
      (this.progress.applicant < 100 ||
        this.progress.anagraphics < 100 ||
        this.progress.requests < 100) &&
      this.progress.score < 100
    );
  }

  get presentationSheetDisabled() {
    return this.progress.score < 100;
  }

  get isFrozenOrConfermata() {
    return (
      this.isFrozen ||
      this.selectedPratica?.state === StatoPratica.CONFERMATA ||
      !!this.selectedPratica?.productDetail?.score ||
      this.isOperatorInRichiestaDocumentale
    );
  }

  /*   get isCloned() {
    return !!this.selectedPratica?.isCloned && this.isCmcOrCdcCoo;
  } */

  get isCmcOrCdcCoo() {
    return this.auth.isCoordinatore && (this.auth.isCdc || this.auth.isAml);
  }

  get selectedPratica() {
    return Object.freeze(this._selectedPratica.value.data);
  }

  get canEditDataOnSignedPratica() {
    return (
      this.auth.isCdc &&
      (this.selectedPratica?.state === StatoPratica.FIRMATA ||
        this.selectedPratica?.state === StatoPratica.IN_ASSEGNAZIONE ||
        this.selectedPratica?.state ||
        StatoPratica.DOCUMENTAZIONE_INTEGRATA)
    );
  }

  resetPratica() {
    this._selectedPratica.next(this._selectedPratica.value.update('init'));
    this._sectionHasUpdated$.next(0);
    this.hasJustBeenSigned.next(false);
    this._praticaProductsData.next(
      this._praticaProductsData.value.update('init')
    );
  }

  get progress() {
    return this.progress$.value;
  }

  calcComplexCompletionPercentage(
    componentCompletionPercentage: number,
    percentageWeight: number
  ) {
    let completionPercentage = componentCompletionPercentage * percentageWeight;
    let roundedPercentage = Math.round(completionPercentage);
    return roundedPercentage;
  }

  private performPraticaStateChecks(pratica: Pratica) {
    let yesStates = [
      StatoPratica.APERTA,
      StatoPratica.CONFERMATA,
      StatoPratica.COMPLETATA,
    ];
    let isOperatorInIntegrazioneDocumentale =
      pratica.state === StatoPratica.IN_RICHIESTA_DOCUMENTALE &&
      this.auth.isOperator;
    let isOperatorInAggiuntaGarante =
      pratica.state === StatoPratica.AGGIUNTA_GARANTE && this.auth.isOperator;
    let praticaIsLocked =
      (!yesStates.includes(pratica.state) ||
        (pratica.state === StatoPratica.APERTA &&
          pratica.isCloned &&
          !this.isCmcOrCdcCoo) ||
        (pratica.state === StatoPratica.COMPLETATA && !this.isCmcOrCdcCoo)) &&
      !isOperatorInIntegrazioneDocumentale &&
      !isOperatorInAggiuntaGarante;
    let praticaIsAssignedToUser = pratica?.user === this.auth.me?.id;
    let isReadOnlyUser: boolean;
    let initialStates = [
      StatoPratica.APERTA,
      StatoPratica.NON_CONSENTITA,
      StatoPratica.CONFERMATA,
      StatoPratica.IN_FIRMA,
    ];

    if (
      this.auth.isAmministratore ||
      this.auth.isDeveloper ||
      this.auth.isAnalista
    ) {
      isReadOnlyUser = true;
    } else {
      if (this.auth.isBackOffice) {
        isReadOnlyUser = false;
      } else {
        isReadOnlyUser = !praticaIsAssignedToUser;
      }
    }
    this.isFrozen = praticaIsLocked || isReadOnlyUser;

    let isInitState = initialStates.includes(pratica.state);
    let isAnalisiCreditoRole =
      this.auth.isCoordinatore ||
      this.auth.isAnalista ||
      this.auth.isBackOffice ||
      (this.auth.isCdc && this.auth.isAmministratore);
    this.isOutcome =
      !isInitState && isAnalisiCreditoRole && pratica.type !== 'AML';
  }

  get isBackOfficeChannelInOutcome() {
    return this.isOutcome && this.auth.isBackOffice && !this.auth.isCdc;
  }

  get isOperatorInRichiestaDocumentale() {
    return (
      this.selectedPratica?.state === StatoPratica.IN_RICHIESTA_DOCUMENTALE &&
      this.auth.isOperator &&
      this.selectedPratica.user === this.auth.me?.id
    );
  }

  get isOperatorInAggiuntaGarante() {
    return (
      this.selectedPratica?.state === StatoPratica.AGGIUNTA_GARANTE &&
      this.auth.isOperator &&
      this.selectedPratica.user === this.auth.me?.id
    );
  }

  editDocumentsSectionProgress(
    praticaId: string,
    currentPraticaProgress: Partial<Record<PraticaSection, number>>,
    documentsProgress: number
  ) {
    let newProgress = {
      ...currentPraticaProgress,
      documents: documentsProgress,
    };
    return this.edit(praticaId, { progress: newProgress });
  }

  get isPraticaCloned() {
    return this.selectedPratica?.isCloned;
  }
}
